/**
 * Form helpers.
 *
 * `submitFormUsingFetch()` AJAX-submits an HTML form to the form's own `action`
 * URL — including cross-domain actions — keeping native HTML-form submit
 * semantics. `dispatchElement()` can't serve that: it dispatches to an xlib
 * element method in the same app, not to an arbitrary form `action`.
 *
 * `formDataToObject()` turns FormData into an object, grouping `[]`-suffixed keys
 * into arrays (getAll). Current elements (Search/SearchResultsPlugin,
 * ThirdParty/NewsletterSingle) use it to build the `formValues` they submit via
 * `dispatchElement()`.
 *
 * @see Also imported by web-reichweiten, wp-cosi, wp-premium (2026-07-30).
 */
export default class FormUtils
{
    /**
     * @deprecated use submitFormUsingFetch() — this no longer uses Ajax/XHR.
     * @param {Element} form
     * @param {string} responseType
     * @returns {Promise<{data: *, status: number, ok: boolean}>}
     */
    static submitFormUsingAjax(form, responseType = 'json')
    {
        return FormUtils.submitFormUsingFetch(form, responseType);
    }

    /**
     * @param {Element} form
     * @param {string} responseType
     * @returns {Promise<{data: *, status: number, ok: boolean}>}
     */
    static async submitFormUsingFetch(form, responseType = 'json')
    {
        const method = (form.getAttribute('method') || 'get').toUpperCase();
        const formData = new FormData(form);
        const url = FormUtils.actionUrlFromAttribute(form);
        const init = { method };

        if (method === 'GET') {
            // GET: form fields become the query string, no body.
            url.search = new URLSearchParams(formData);
        } else {
            // POST: native multipart body — preserves file inputs (browser sets the boundary).
            init.body = formData;
        }

        const response = await fetch(url, init);
        if (!response.ok) {
            throw new Error(`Form submit to ${url} failed with status ${response.status}`);
        }

        let data;
        switch (responseType) {
            case 'text': data = await response.text(); break;
            case 'blob': data = await response.blob(); break;
            case 'arraybuffer': data = await response.arrayBuffer(); break;
            default: data = await response.json();
        }

        return { data, status: response.status, ok: response.ok };
    }

    /**
     * Reads the `action` attribute because `form.action` is shadowed by a control
     * named "action" (e.g. `<input name="action">`). Falls back to the document URL
     * like a native submit does when the attribute is missing or empty.
     *
     * @param {HTMLFormElement} form
     * @returns {URL}
     */
    static actionUrlFromAttribute(form)
    {
        return new URL(form.getAttribute('action') || document.URL, document.baseURI);
    }

    /**
     * @param {FormData} formData
     * @returns {Object<string, string|array>}
     */
    static formDataToObject(formData)
    {
        const formValues = {};
        for (const inputName of formData.keys()) {
            if (inputName.endsWith(']')) {
                formValues[inputName] = formData.getAll(inputName);
            } else {
                formValues[inputName] = formData.get(inputName);
            }
        }

        return formValues;
    }

    /**
     * Serialize a params object to a query string, matching axios's decoded output.
     * Array values repeat under a bracketed key (only_dispatch[]=…); keys that already
     * carry the brackets (category[]) are kept as-is. null/undefined are omitted, as axios.
     *
     * @param {Record<string, *>} params
     * @returns {string} query string without a leading '?'
     */
    static objectToQueryString(params)
    {
        const searchParams = new URLSearchParams();
        for (const [key, value] of Object.entries(params)) {
            if (value === null || value === undefined) {
                continue;
            }
            const paramKey = key.endsWith(']') ? key : `${key}[]`;
            if (Array.isArray(value)) {
                value
                    .filter(item => item !== null && item !== undefined)
                    .forEach(item => searchParams.append(paramKey, item));
            } else {
                searchParams.append(key, value);
            }
        }

        return searchParams.toString();
    }
}


